import { type ReactNode, useMemo } from 'react';

import { RiLoader4Line } from '@remixicon/react';

import { Text } from '@spotify-internal/encore-web';
import {
  AiLoadingIndicator,
  type ItemRenderExtensionOptions,
} from '@spotify/confidence-core-react';
import {
  ChatEventFragment,
  ConversationsV2ToolConfirmationResponseDecision,
} from '@spotify/confidence-graphql';
import {
  ChatContextProvider,
  type DisplayItem,
  ToolCallGroup,
  buildInlineBlocksIndex,
  groupChatEvents,
  stripMarkdownLinks,
  useCachedChatEvents,
  useChatEventExtensions,
  useToolExtensions,
} from '@spotify/plugin-confidence-chat-react';

import { getIssueChatName } from '../../utils/issueChatUtils';

import styles from './TaskLatestChatEvent.module.css';

const RUNNING_EVENT_POLL_INTERVAL_MS = 1000;

type ChatEventExtension = ItemRenderExtensionOptions<ChatEventFragment>;

const isDisplayItemRenderable = (
  item: DisplayItem,
  chatEventExtensions: ChatEventExtension[],
): boolean => {
  if (item.type === 'tool-group') {
    return item.entries.length > 0;
  }

  if (item.event.thinking?.content) {
    return true;
  }

  return chatEventExtensions.some(chatEventExtension =>
    chatEventExtension.predicate(item.event),
  );
};

const findLatestRenderableDisplayItem = (
  displayItems: DisplayItem[],
  chatEventExtensions: ChatEventExtension[],
): DisplayItem | null => {
  for (let index = displayItems.length - 1; index >= 0; index -= 1) {
    const item = displayItems[index];
    if (isDisplayItemRenderable(item, chatEventExtensions)) {
      return item;
    }
  }

  return null;
};

const getPreviewKey = (item: DisplayItem): string => {
  if (item.type === 'tool-group') {
    return item.entries.at(-1)?.eventName ?? item.key;
  }

  return item.key;
};

const toSingleLinePreviewText = (text: string): string =>
  text.replace(/\s+/g, ' ').trim();

const getEventPreviewText = (event: ChatEventFragment): string => {
  if (event.thinking?.content) {
    return toSingleLinePreviewText(event.thinking.content);
  }

  if (event.toolConfirmationRequest) {
    const request = event.toolConfirmationRequest;
    return stripMarkdownLinks(
      request.displayName ?? request.tool.replace(/_/g, ' '),
    );
  }

  if (event.toolConfirmationResponse) {
    const request =
      event.toolConfirmationResponse.toolRequestEvent?.__typename ===
      'ConversationsV2ChatEvent'
        ? event.toolConfirmationResponse.toolRequestEvent
            .toolConfirmationRequest
        : undefined;
    const toolLabel = stripMarkdownLinks(
      request?.displayName ?? request?.tool.replace(/_/g, ' ') ?? 'Tool',
    );
    const approved =
      event.toolConfirmationResponse.decision !==
      ConversationsV2ToolConfirmationResponseDecision.Deny;

    return approved ? `Approved ${toolLabel}` : `Cancelled ${toolLabel}`;
  }

  if (event.notification?.childChatStopped?.summary) {
    return toSingleLinePreviewText(event.notification.childChatStopped.summary);
  }

  if (event.assistantMessage?.content) {
    return toSingleLinePreviewText(event.assistantMessage.content);
  }

  if (event.error?.message) {
    return toSingleLinePreviewText(event.error.message);
  }

  if (event.userInterrupt) {
    return 'Stopped';
  }

  return 'Working…';
};

const TaskLatestChatEventPreview = ({ children }: { children: ReactNode }) => (
  <div className={styles.taskLatestChatEventPreview}>{children}</div>
);

const toSingleEntryDisplayItem = (item: DisplayItem): DisplayItem => {
  if (item.type !== 'tool-group') {
    return item;
  }

  const lastEntry = item.entries.at(-1);
  if (!lastEntry) {
    return item;
  }

  return {
    type: 'tool-group',
    entries: [lastEntry],
    key: lastEntry.eventName ?? item.key,
  };
};

type LatestChatEventContentProps = {
  item: DisplayItem;
};

const LatestChatEventContent = ({ item }: LatestChatEventContentProps) => {
  if (item.type === 'tool-group') {
    return (
      <TaskLatestChatEventPreview>
        <ToolCallGroup entries={item.entries} variant="inline" />
      </TaskLatestChatEventPreview>
    );
  }

  const isThinking = Boolean(item.event.thinking?.content);

  return (
    <TaskLatestChatEventPreview>
      {isThinking && (
        <RiLoader4Line
          size={14}
          aria-hidden
          className={styles.taskLatestChatEventPreviewIcon}
        />
      )}
      <Text
        as="span"
        variant="bodySmall"
        semanticColor="textSubdued"
        lineClamp={1}
        className={styles.taskLatestChatEventPreviewText}
      >
        {getEventPreviewText(item.event)}
      </Text>
    </TaskLatestChatEventPreview>
  );
};

export type TaskLatestChatEventProps = {
  issueName: string;
};

export const TaskLatestChatEvent = ({
  issueName,
}: TaskLatestChatEventProps) => {
  const chatName = getIssueChatName(issueName);
  const { chatEvents } = useCachedChatEvents(chatName ?? '', {
    pollInterval: RUNNING_EVENT_POLL_INTERVAL_MS,
  });
  const toolExtensions = useToolExtensions();
  const chatEventExtensions = useChatEventExtensions();

  const latestItem = useMemo(() => {
    if (!chatName || chatEvents.length === 0) {
      return null;
    }

    const displayItems = groupChatEvents(chatEvents, toolExtensions);
    const renderableItem = findLatestRenderableDisplayItem(
      displayItems,
      chatEventExtensions,
    );

    return renderableItem ? toSingleEntryDisplayItem(renderableItem) : null;
  }, [chatEvents, chatEventExtensions, chatName, toolExtensions]);

  const previewKey = latestItem ? getPreviewKey(latestItem) : null;

  const chatContextValue = useMemo(
    () => ({
      chatName: chatName ?? '',
      chatEvents,
      inlineBlocks: buildInlineBlocksIndex(chatEvents),
    }),
    [chatEvents, chatName],
  );

  if (!chatName) {
    return null;
  }

  return (
    <ChatContextProvider value={chatContextValue}>
      <div className={styles.taskLatestChatEvent}>
        {!latestItem ? (
          <AiLoadingIndicator />
        ) : (
          <div key={previewKey} className={styles.taskLatestChatEventItem}>
            <LatestChatEventContent item={latestItem} />
          </div>
        )}
      </div>
    </ChatContextProvider>
  );
};
